import {ParsableDate} from '@material-ui/pickers/constants/prop-types';
import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TransHelper} from '../../../utils/trans-helper';
import NxButton, {MaterialButtonVariant} from '../nxButton/NxButton';
import NxDatePicker, {DEFAULT_DATE_PICKER_VALUE_FORMAT} from '../nxDatePicker/NxDatePicker';
import NxSelect, {NxSelectOption} from '../nxSelect/NxSelect';
import styles from './Filter.module.scss';
import dayjs from 'dayjs';
import {Formik} from 'formik';
import {SchemaOf, object, string} from 'yup';

export interface FiltersProps {
  className?: string;
  onChange: (values: FilterParams) => void;
  value?: string;
  selectOptions: NxSelectOption[];
  disableFutureDates?: boolean;
  isCurrentDate?: boolean;
  defaultDateFrom?: ParsableDate;
  defaultDateTo?: ParsableDate;
  maxDateTo?: ParsableDate;
}

export type FilterParams = {
  dateFrom?: string;
  dateTo?: string;
  status?: string;
};

const emptyFilterInputValues: FilterInputs = {
  dateFrom: '',
  dateTo: '',
  status: 'EXECUTED'
};

type FilterInputs = Required<FilterParams>;

const PrefixTrans = TransHelper.getPrefixedTrans('FILTERS');
const SharedPrefixTrans = TransHelper.getPrefixedTrans('SHARED');

const filedRequiredErrorMessage = 'SHARED.ERROR_MESSAGE.FIELD_REQUIRED';
const dateToday = dayjs().format(DEFAULT_DATE_PICKER_VALUE_FORMAT);

function Filters({
  onChange,
  selectOptions,
  disableFutureDates,
  defaultDateFrom = dateToday,
  defaultDateTo = dateToday,
  maxDateTo
}: FiltersProps): ReactElement {

  const {t} = useTranslation();

  const filterInputsInitValues: FilterInputs = {
      ...emptyFilterInputValues,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo
  } as FilterInputs;

  const validationSchema: SchemaOf<FilterParams> = object({
    dateFrom: string().defined(t(filedRequiredErrorMessage)),
    dateTo: string()
      .defined(t(filedRequiredErrorMessage))
      .test(
        'maxDays',
        t('SHARED.ERROR_MESSAGE.MAX_DAYS', {maxDays: 31}),
        (date, context) => {
          const dateFrom = dayjs(context.parent.dateFrom);
          const maxDaysAdded = dateFrom.add(30, 'day');
          const dateTo = dayjs(date);

          return dateTo.isBefore(maxDaysAdded) || maxDaysAdded.isSame(dateTo);
        }
      )
      .test(
        'validDateRange',
        t('SHARED.ERROR_MESSAGE.INVALID_DATE_RANGE'),
        (date, context) => {
          if (!context.parent.dateFrom) {
            return true;
          }

          const dateFrom = dayjs(context.parent.dateFrom);
          const dateTo = dayjs(date);

          return dateFrom.isBefore(dateTo) || dateFrom.isSame(dateTo);
        }
      ),
    status: string()
      .oneOf(
        [...selectOptions.map((o) => o.value), undefined, ''],
        t('SHARED.ERROR_MESSAGE.INVALID_OPTION')
      )
  }).defined();

  return (
    <Formik<FilterParams>
      initialValues={filterInputsInitValues}
      validationSchema={validationSchema}
      onSubmit={onChange}>
      {({
        values,
        errors,
        handleSubmit,
        setFieldValue,
        resetForm
      }) => (
        <form className={styles.filterWrapper} onSubmit={handleSubmit}>
          <div>
            <NxDatePicker
              label={''}
              className={styles.filterInput}
              maxDate={values.dateTo || undefined}
              value={values.dateFrom}
              disableFutureDates={disableFutureDates}
              placeholder={t('FILTERS.INPUT.DATE_FROM.PLACEHOLDER')}
              onChange={(date) => setFieldValue('dateFrom', date || '')} />
            <div className={styles.error}>{errors.dateFrom}</div>
          </div>
          <div>
            <NxDatePicker
              label={''}
              className={styles.filterInput}
              minDate={values.dateFrom || undefined}
              value={values.dateTo}
              disableFutureDates={disableFutureDates}
              placeholder={t('FILTERS.INPUT.DATE_TO.PLACEHOLDER')}
              maxDate={maxDateTo}
              onChange={(date) => setFieldValue('dateTo', date || '')} />
            <div className={styles.error}>{errors.dateTo}</div>
          </div>
          <div>
            <NxSelect
              options={selectOptions}
              label={<SharedPrefixTrans>STATUS</SharedPrefixTrans>}
              className={styles.filterInput}
              value={values.status}
              onChange={(status) => setFieldValue('status', status || '')} />
            <div className={styles.error}>{errors.status}</div>
          </div>
          <NxButton type='submit' className={styles.button}>
            <PrefixTrans>APPLY_FILTER</PrefixTrans>
          </NxButton>
          <NxButton
            type='button'
            variant={MaterialButtonVariant.OUTLINED}
            onClick={() => resetForm({values: emptyFilterInputValues})}>
            <PrefixTrans>CLEAR_FILTER</PrefixTrans>
          </NxButton>
        </form>
      )}
    </Formik>
  );
}

export default Filters;
